import _ from 'underscore';
import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { WalletCreditCard20Regular } from '@fluentui/react-icons';
import { StatusSummaryCard } from '@bingads-webui-campaign-react/status-summary-card';
import { BudgetComponent } from '@bingads-webui-campaign-react/budget-component';
import { FORM_STATES, Form, useFormService } from '@bingads-webui-react/form';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { Budget } from '@bingads-webui/campaign-odata-schemas';
import { FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { BudgetSuggestion, MODE, useBudgetSuggestionRepository } from '@bingads-webui-campaign-react/budget-suggestion-v2';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { LinkedInBudgetRangePerCurrency } from '@bingads-webui-campaign-legacy/common-grid-config/budget/get-budget-range';

import { withFooter } from './with-footer';
import { useBudgetRepository } from './use-repository';
import { useReadCampaign } from './use-read-campaign';
import { getApiErrorMapper } from '../utils/api-error-mapper';
import { useBudgetCardStyles } from '../styles/budget-card-styles';

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const parseNumber = (value: any): any => {
  if (typeof value === 'string') {
    return value.replace(/,/g, ''); // Remove commas from strings
  }
  return value; // Return the input unchanged for other types
};

const FormViewWrapper = ({
  data,
  onDataChange,
  logger,
  error,
}: { data: any; onDataChange: Function; logger: Function; error: any; }) => {
  const {
    campaignType,
    budgetAmount,
    currency,
    bidStrategy,
    // deliveryStatus,
    // deliveryStatusDetails,
  } = data || {};
  const errors = error?.error?.errors || [];
  const i18n = useContext(I18nContext) as any;
  const serverContext = useContext(ServerContext) as any;

  const { scope } = useContext(ScopeContext) as any;
  const { campaignId } = scope.values;
  const classes = useBudgetCardStyles();
  const { state } = useFormService();
  const showBudgetSuggestion = serverContext?.DynamicPermissions?.EnableBudgetSuggestion && campaignType === 'PerformanceMax';
  const isLoading = state.matches(FORM_STATES.LOADING);
  const [isBSLoading, setIsBSLoading] = useState<boolean>(true);
  const [isBSCalled, setIsBSCalled] = useState<boolean>(false);

  const apiErrorMapper = getApiErrorMapper(i18n);

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const mergedI18n = useMemo(() => mergeI18n(
    legacyI18n,
    i18n
  ), [i18n, legacyI18n]);

  const schema = useMemo(() => {
    if (campaignType === 'LinkedIn' && LinkedInBudgetRangePerCurrency[currency]) {
      return ({
        Currency: currency,
        GlobalMaximumBudget: { DailyBudget: LinkedInBudgetRangePerCurrency[currency].Max },
        GlobalMinimumBudget: { DailyBudget: LinkedInBudgetRangePerCurrency[currency].Min },
        MaximumBudget: { DailyBudget: LinkedInBudgetRangePerCurrency[currency].Max }, // we need to pass this as a max for the slider
      });
    }

    // Currently the max daily budget returned by server context is the same as max monthly budget https://msasg.visualstudio.com/Bing_Ads/_git/AdsAppUI?path=/private/UI/OpportunityUI/Microsoft.AdCenter.UI.Web.Opportunity/Repository/StaticEntityRepository.cs&version=GBmaster&line=86&lineEnd=86&lineStartColumn=13&lineEndColumn=122&lineStyle=plain&_a=contents
    // Because of that we use the same logic as on MT where maxDailyBudget = maxMonthlyBudget / 28 https://msasg.visualstudio.com/DefaultCollection/Bing_Ads/_git/AdsAppsMT?path=/private/Common/BusinessRules/CampaignValidators/BudgetLimitHelper.cs&version=GBmaster&line=10&lineEnd=12&lineStartColumn=1&lineEndColumn=35&lineStyle=plain&_a=contents
    const maxDailyBudget = serverContext?.BudgetRange?.MonthlyRange?.CeilingValue ? Number((serverContext?.BudgetRange?.MonthlyRange?.CeilingValue / 28).toFixed(2)) : serverContext?.BudgetRange?.DailyRange?.CeilingValue;
    return ({
      Currency: currency,
      GlobalMaximumBudget: { DailyBudget: maxDailyBudget },
      GlobalMinimumBudget: { DailyBudget: serverContext?.BudgetRange?.DailyRange?.FloorValue },
      MaximumBudget: { DailyBudget: maxDailyBudget }, // we need to pass this as a max for the slider
    });
  }, [currency, serverContext, campaignType]);

  const onFieldChange = useCallback((updatedData) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...updatedData,
      }),
    });
  }, [onDataChange]);

  const localizeBudget = useCallback((budget, shouldLocalize) => (
    shouldLocalize ? i18n.formatCurrency(
      Number(budget), currency.toUpperCase(),
      { withSymbol: false }
    ) : budget), [currency, i18n]);

  const onBudgetChange = useCallback((newBudget, shouldLocalize = false) => {
    const budgetAmountValue = localizeBudget(newBudget, shouldLocalize);
    onFieldChange({ budgetAmount: budgetAmountValue });
  }, [onFieldChange, localizeBudget]);

  const onChange = useCallback((newBudget, shouldLocalize = false) => {
    if (newBudget?.data?.budget && newBudget.isValid) {
      const budgetVal = localizeBudget(String(newBudget.data.budget), shouldLocalize);
      onFieldChange({ budgetAmount: budgetVal, isBudgetValid: newBudget.isValid, budgetErrors: newBudget.messages });
    }
    if (newBudget && !newBudget.isValid) {
      onFieldChange({ isBudgetValid: newBudget.isValid, budgetErrors: newBudget.messages });
    }
  }, [onFieldChange, localizeBudget]);

  const onBudgetValidate = useCallback(({ isValid, messages }) => {
    onFieldChange({ isBudgetValid: isValid, budgetErrors: messages });
  }, [onFieldChange]);

  const [budgetSuggestionData, setBudgetSuggestionData] = useState(null);

  const { loadBudget } = useBudgetSuggestionRepository({
    campaignId,
    campaignType,
    currency,
    campaign: data,
  });

  useEffect(() => {
    if (showBudgetSuggestion) {
      if (_.isEmpty(budgetSuggestionData) && data && campaignId && !isBSCalled) {
        loadBudget().then((datas) => {
          setBudgetSuggestionData(datas);
        }).finally(() => {
          setIsBSLoading(false);
          setIsBSCalled(true);
        });
      }
    } else {
      setIsBSLoading(isLoading);
    }
  }, [loadBudget, budgetSuggestionData, campaignId, data, isLoading, showBudgetSuggestion, isBSCalled]);

  //  const showBudgetSuggestion = serverContext?.DynamicPermissions?.EnableBudgetSuggestion &&
  // (deliveryStatus === 'BudgetSuggestions' || _.includes(deliveryStatusDetails, 'BudgetSuggestions'));
  const showBudgetSuggestionData = showBudgetSuggestion && !_.isEmpty(budgetSuggestionData);

  return (
    <div className={classes.container}>
      <FormErrorGroup formErrorProps={{ errors, errorMapper: apiErrorMapper }}>
        <ValidationGroup
          validationProps={{
            i18n: mergedI18n,
            schema: Budget,
            data,
            onValidate: () => { },
          }}
        >{
          !isBSLoading &&
          <div>
            {!showBudgetSuggestionData &&
              <BudgetComponent
                currency={currency}
                budget={parseNumber(budgetAmount)}
                onBudgetChange={onBudgetChange}
                isLoading={isLoading}
                onNotifyValidation={onBudgetValidate}
                logger={logger}
                budgetSuggestion={schema}
                withSlider={false}
              />
          }
            {showBudgetSuggestionData &&
              <div>
                {i18n.getString(_TL_('Your ads could be shown more often. You can increase your budget to ensure more ad exposure.'))}
                <LearnMoreLink
                  linkText={i18n.getString(_TL_('Learn more about changing a limited or out of budget delivery status.'))}
                  topic={2282368}
                  isUnderlineLink={undefined}
                  isGreyBackground={undefined}
                />
                <BudgetSuggestion
                  mode={MODE.EDITING}
                  onBudgetValidate={onChange}
                  campaignId={campaignId}
                  campaignType={campaignType}
                  currency={currency}
                  bidType={bidStrategy}
                  minBudget={schema.GlobalMinimumBudget.DailyBudget}
                  maxBudget={schema.GlobalMaximumBudget.DailyBudget}
                />
              </div>
          }
          </div>
        }
          <UnmappedFormErrors className="server-errors" />
        </ValidationGroup>
      </FormErrorGroup>
    </div>
  );
};

const BudgetForm = ({ closeDialog, log, showBudgetSuggestion }: { closeDialog: Function, log: { trace: Function }, showBudgetSuggestion: Boolean}) => {
  const onDoneSaving = useCallback((...params) => {
    log.trace('Budget form popover closed: saved');
    closeDialog(...params);
  }, [log, closeDialog]);

  const onCanceled = useCallback((...params) => {
    log.trace('Budget form popover closed: canceled');
    closeDialog(...params);
  }, [log, closeDialog]);

  useEffect(() => {
    log.trace('Budget card popover rendered');
  }, [log]);

  const config = useMemo(() => ({
    id: 'budget-form',
    view: withFooter(FormViewWrapper, showBudgetSuggestion),
    repository: useBudgetRepository,
    machineOptions: {
      actions: {
        onDoneSaving,
        onCanceled,
      },
    },
  }), [onCanceled, onDoneSaving, showBudgetSuggestion]);
  return (<Form config={config} />);
};

export const BudgetCard: React.FunctionComponent<{ contentReady: Function }> = ({ contentReady }: { contentReady: Function }) => {
  const i18n = useContext(I18nContext) as any;
  const instrumentation = useContext(InstrumentationContext) as any;
  const serverContext = useContext(ServerContext) as any;
  const currency = serverContext?.CurrentAccount?.CurrencyCode;

  const log = useMemo(() => {
    if (instrumentation && instrumentation.campaignOverviewEdit) {
      const scenario = instrumentation.campaignOverviewEdit.create();
      return scenario.budgetEdit.create();
    }
    return {
      trace: _.noop,
      error: _.noop,
    };
  }, [instrumentation]);

  const [budgetText, setBudgetText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [budgetTitle, setBudgetTitle] = useState<string>(i18n.getString(_TL_('Set campaign budget')));
  const [isBSCalled, setIsBSCalled] = useState<boolean>(false);

  const [campaignInfo, setCampaignInfo] = useState<any>(null);

  const [budgetSuggestionData, setBudgetSuggestionData] = useState<any>(null);
  const { loadBudget } = useBudgetSuggestionRepository({
    campaignId: campaignInfo?.Id,
    campaignType: campaignInfo?.campaignType,
    currency,
    campaignInfo,
  });


  useEffect(() => {
    if (serverContext?.DynamicPermissions?.EnableBudgetSuggestion &&
        !isBSCalled &&
        _.isEmpty(budgetSuggestionData) && campaignInfo && campaignInfo?.Id) {
      loadBudget().then((dataBs) => {
        setBudgetSuggestionData(dataBs);
        setBudgetTitle(i18n.getString(_TL_('Budget recommendations')));
      }).finally(() => {
        setIsLoading(false);
        setIsBSCalled(true);
      });
    }
  }, [loadBudget, campaignInfo, budgetSuggestionData, serverContext?.DynamicPermissions?.EnableBudgetSuggestion, isBSCalled, i18n]);

  const set = useCallback((campaign) => {
    const localizedBudget = i18n.formatCurrency(
      Number(campaign?.Budget?.Amount), currency.toUpperCase(),
      { withSymbol: false }
    );
    setCampaignInfo(campaign);
    setBudgetText(i18n.getString(_TL_('{{budget}} {{currency}}/Day'), { currency: currency.toUpperCase(), budget: localizedBudget }));
    if (!serverContext?.DynamicPermissions?.EnableBudgetSuggestion) {
      setIsLoading(false);
    }
    contentReady();
  }, [contentReady, currency, i18n, serverContext?.DynamicPermissions?.EnableBudgetSuggestion]);


  const readCampaign = useReadCampaign({ set });
  useEffect(() => {
    setIsLoading(true);
    readCampaign();
  }, [currency, i18n, readCampaign, contentReady]);

  useEffect(() => {
    log.trace('Budget card rendered');
  }, [log]);

  const ChildComponent = useMemo(() => (props => <BudgetForm {...props} log={log} showBudgetSuggestion={!_.isEmpty(budgetSuggestionData)} />), [log, budgetSuggestionData]);

  return (
    <StatusSummaryCard
      header={i18n.getString(_TL_('Budget'))}
      description={budgetText}
      IconComponent={WalletCreditCard20Regular}
      color="purple"
      isLoading={isLoading}
      dialogEditProps={{
        title: budgetTitle,
        ChildComponent,
        buttonAriaLabel: i18n.getString(_TL_('Edit budget')),
      }}
      className="budget-edit-card"
      warningMessage={!_.isEmpty(budgetSuggestionData) && i18n.getString(_TL_('Limited'))}
    />
  );
};
